/**
 * stuburt-popup.js — shared overlay behaviour for the Stuburt popups
 * (welcome / email-capture modal and the "before you leave" exit panel).
 *
 * One Custom Element, configured per instance via attributes so both sections
 * share the same logic without duplicating it:
 *   trigger="load|exit"   when to show (timer on load, or exit-intent)
 *   delay="<ms>"          load delay, or exit fallback timer (0 = none)
 *   disable-below="<px>"  never arm for real visitors at or under this viewport
 *                         width (blank/absent = show at every width)
 *   storage-key="<str>"   localStorage key used to show it once per window
 *   frequency-days="<n>"  how long a dismissal/view suppresses re-showing
 *   requires-email        (boolean) reveal the code only after the form posts
 *   error-text="<str>"    message shown if the email post fails, when the
 *                         reloaded page carries no server-side message of its own
 *   data-section-id="..." section id, so the editor can preview on select
 *   choice-storage-key="<str>"  present when the popup opens on a question
 *                         (step 1) instead of the email field. The answer sets
 *                         data-choice on the host, which CSS uses to swap in
 *                         that branch's copy, and is remembered under this key
 *                         so a returning visitor isn't asked twice.
 *   ab-test="<id>"        A/B test id. Present only while a test runs.
 *   ab-arms="a:80,b:10"   arm:weight pairs. Each visitor is put in one arm at
 *                         random, once, and stays there (see #applyArm).
 *   ab-editor-arm="<arm>" the arm the theme editor previews.
 *
 * Preview one arm on the storefront: ?preview_popup=welcome&popup_arm=mystery.
 * A forced arm is never stored and never counted.
 *
 * Preview without incognito: load any page with ?preview_popup=welcome or
 * ?preview_popup=exit and the matching popup opens immediately, ignoring the
 * once-per-window suppression (and without writing it, so real visits are
 * unaffected).
 *
 * The email form posts NATIVELY and the page reloads — see #submit for why it
 * cannot be AJAX-posted — so the result is picked back up from server state on
 * the next load by #resumeAfterSubmit.
 *
 * Non-negotiables honoured: vanilla Custom Element (no framework), full
 * teardown in disconnectedCallback, reduced-motion respected (CSS owns the
 * animation; JS only toggles state), and the editor lifecycle is handled —
 * in design mode the popup opens when its section is selected and closes on
 * deselect, so it never sits on top of the customizer.
 */

// Only one Stuburt popup may be on screen at a time. The welcome modal and the
// "before you leave" exit panel are the same custom element sharing this module,
// so this module-level reference is a mutex across every instance on the page —
// it guarantees the two can never be open simultaneously. Real visitors only;
// the editor and ?preview_popup force-open on purpose and bypass it.
let openPopup = null;

// Set by assets/cart-discount.js the moment a discount code is successfully
// applied. These popups exist to hand out a code, so once the customer has one
// on their cart the offer is spent — this suppresses them permanently, ignoring
// the per-popup frequency window entirely.
const OFFER_CLAIMED_KEY = 'stuburt_offer_claimed';

// Written to sessionStorage immediately before the native submit, and read back
// on the reloaded page. It exists because Shopify marks EVERY
// {% form 'customer' %} on that page as posted — the footer signup as well as
// this one — so the server-rendered marker alone cannot say which form the
// visitor actually used. The stamp names the popup that posted.
// stuburt-signup-toast.js reads the same key to stay quiet when the popup, not
// the footer, was the source; keep the key and the window in step with that file.
const SUBMIT_STAMP_KEY = 'stuburt_popup_submitted';
const SUBMIT_STAMP_MAX_AGE = 60000;

// The revealed code has to outlive this element. The submit stamp above is
// deliberately single-use, so on its own it cannot put the reveal back if
// anything rebuilds the popup — a theme-editor section re-render, the dev
// server's hot reload, an accidental refresh. That left the visitor looking at
// the opening question with the code gone and no way back to it, since the code
// is only ever shown here. So the reveal records itself and restores on connect.
// Only close() retires it: "stays up until they dismiss it" is the contract.
// Scoped to the path it was revealed on, so it doesn't trail them round the site.
const REVEAL_KEY = 'stuburt_popup_revealed';
const REVEAL_MAX_AGE = 1800000;

// The visitor's A/B arm, stored with the test id so a new test re-splits
// everyone. localStorage, not session: an arm that changed between visits would
// make a returning visitor's sign-up count against the wrong version.
const AB_KEY = 'stuburt_popup_ab';

// The one arm that opens on the question. Every other arm goes straight to the
// email step, so the question is the only thing separating it from 'plain'.
const AB_GATE_ARM = 'fitd';

class StuburtPopup extends HTMLElement {
  #abort = null;
  #timer = null;
  #open = false;
  #lastFocus = null;
  #prevOverflow = '';
  #previewMode = false;
  #consentTimer = null;
  #arm = null;
  #armForced = false;
  #resumed = false;

  connectedCallback() {
    this.dialog = this.querySelector('[data-popup-dialog]');
    this.form = this.querySelector('form');
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Close: X button, overlay click, ESC.
    this.addEventListener(
      'click',
      (event) => {
        if (
          event.target.closest('[data-popup-close]') ||
          event.target.hasAttribute('data-popup-overlay')
        ) {
          event.preventDefault();
          this.close(true);
        }
        const copyBtn = event.target.closest('[data-popup-copy]');
        if (copyBtn) this.#copy(copyBtn);
        // Step 1 of the two-step welcome popup. Absent on the exit panel, which
        // has no [data-popup-choice] buttons and so never enters this branch.
        const choiceBtn = event.target.closest('[data-popup-choice]');
        if (choiceBtn) {
          event.preventDefault();
          this.#choose(
            choiceBtn.getAttribute('data-popup-choice'),
            choiceBtn.getAttribute('data-popup-choice-tag'),
            true
          );
        }
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#open) this.close(true);
      },
      { signal }
    );

    // Keep Tab focus inside the open dialog.
    this.addEventListener('keydown', (event) => this.#trapFocus(event), {
      signal,
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.#submit(event), {
        signal,
      });
    }

    // Before anything can open: the arm decides which copy and which step the
    // popup starts on, the reveal and the post-submit resume included.
    this.#applyArm();

    // Rebuilt while the code was on screen — put it straight back. This runs
    // ahead of every other branch, preview and design mode included, because
    // whatever caused the rebuild must never cost the visitor the code.
    if (this.#revealedEarlier()) {
      this.#resumed = true;
      this.#markRevealed(false);
      this.#restoreChoice();
      window.requestAnimationFrame(() => this.open());
      return;
    }

    // Preview override: ?preview_popup=welcome|exit force-opens the matching
    // popup, bypassing suppression and triggers. Doesn't persist, so it only
    // affects this preview, not real visitors.
    const previewName = new URLSearchParams(location.search).get('preview_popup');
    if (previewName && this.#storageKey().includes(previewName.toLowerCase())) {
      this.#previewMode = true;
      window.requestAnimationFrame(() => this.open());
      return;
    }

    // A native submit reloaded the page — re-open onto the result, not the form.
    if (this.#resumeAfterSubmit()) return;

    // In the theme editor: preview on section select, hide on deselect.
    if (window.Shopify && window.Shopify.designMode) {
      const id = this.getAttribute('data-section-id');
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (event.detail && String(event.detail.sectionId) === id) this.open();
        },
        { signal }
      );
      document.addEventListener(
        'shopify:section:deselect',
        (event) => {
          if (event.detail && String(event.detail.sectionId) === id)
            this.close(false);
        },
        { signal }
      );
      return;
    }

    // Phones are excluded when the section asks for it. Exit intent has no
    // touch equivalent, so on a narrow screen the fallback timer is the only
    // trigger left — and a panel that slides over the whole viewport mid-scroll
    // is an interruption, not an offer. The editor and ?preview_popup both
    // return above this, so a merchant can still preview it at any width.
    if (this.#belowDisableWidth()) return;

    if (this.#suppressed()) return;

    const trigger = this.getAttribute('trigger') || 'load';
    const delay = parseInt(this.getAttribute('delay'), 10) || 0;

    if (trigger === 'exit') {
      document.addEventListener(
        'mouseout',
        (event) => {
          // Don't re-fire while open, and — crucially — don't fire again once
          // it has already been shown (this visit persists the suppression
          // window). Without re-checking #suppressed() here, closing it and
          // moving the cursor back to the top would re-open it every time.
          if (this.#open || this.#suppressed()) return;
          // Only a true exit out of the top of the window — not a hover off
          // an element (which keeps relatedTarget) or a sideways/bottom exit.
          if (event.relatedTarget || event.clientY > 0) return;
          this.open();
        },
        { signal }
      );
      if (delay > 0) {
        // The fallback is the only trigger on touch devices (no mouseout), so
        // it must respect the same consent gate as the load trigger — never
        // start the countdown while the cookie banner is still up.
        this.#whenConsentResolved(() => {
          if (this.#open || this.#suppressed()) return;
          this.#timer = window.setTimeout(() => {
            if (!this.#suppressed()) this.open();
          }, delay);
        });
      }
    } else {
      this.#armLoadTrigger(delay);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
    window.clearTimeout(this.#consentTimer);
    if (this.#open) document.documentElement.style.overflow = this.#prevOverflow;
    if (openPopup === this) openPopup = null;
    this.#open = false;
  }

  // Load-trigger arming, gated so it never walls the landing:
  //   1. Never stack over the cookie banner — wait until consent resolves.
  //   2. Paid ad traffic sees product first — an engagement trigger, not a timer.
  #armLoadTrigger(delay) {
    const paid = this.#isPaidVisit();
    this.#whenConsentResolved(() => {
      if (this.#suppressed()) return;
      if (paid) {
        this.#armEngagementTrigger();
      } else {
        // 2s grace: at 400ms this landed as a second modal the instant the
        // cookie banner closed, so visitors met two walls before seeing a
        // product. 10s, then 6s, both read as too late into the journey —
        // 2s still clears the banner (the consent gate above does that work)
        // without letting the visitor get past the offer first.
        this.#timer = window.setTimeout(() => this.open(), Math.max(delay, 2000));
      }
    });
  }

  // Paid ad traffic (utm_source=meta / utm_medium=paid_social) must not hit an
  // email wall on arrival. Persist the flag for the tab session so it still holds
  // after the query string drops on the next click-through.
  #isPaidVisit() {
    try {
      const params = new URLSearchParams(location.search);
      const source = (params.get('utm_source') || '').toLowerCase();
      const medium = (params.get('utm_medium') || '').toLowerCase();
      // 2026-09-13: the boot ads tag utm_source=facebook&utm_medium=paid — same
      // rule, so every paid Meta arrival stands the popup down.
      const paidNow =
        source === 'meta' || source === 'facebook' || medium === 'paid_social' || medium === 'paid';
      if (paidNow) window.sessionStorage.setItem('stuburt_paid_visit', '1');
      return (
        paidNow || window.sessionStorage.getItem('stuburt_paid_visit') === '1'
      );
    } catch (error) {
      return false;
    }
  }

  // The welcome popup must never overlap the cookie-consent banner. The banner
  // dispatches `stb:consent-resolved` when a choice is made (and when it decides
  // not to show at all). The fallback keeps capture working if the consent
  // snippet is absent or its API never loads — it holds off while the banner is
  // still visibly up, but keeps re-checking so it arms as soon as that clears.
  #whenConsentResolved(callback) {
    if (window.__stbConsentResolved) {
      callback();
      return;
    }
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      window.clearTimeout(this.#consentTimer);
      callback();
    };
    document.addEventListener('stb:consent-resolved', fire, {
      once: true,
      signal: this.#abort.signal,
    });
    // Re-check on a poll rather than give up. This was a single setTimeout that
    // returned when it found the banner still up — and never rescheduled, so a
    // visitor who left the cookie banner sitting there was unarmed for the rest
    // of the visit and never saw the popup at all. Now it keeps looking, and
    // fires the moment the banner is gone by ANY route: a choice that dispatched
    // no event, a consent snippet that failed to load, a banner dismissed some
    // other way. While it is genuinely still on screen we go on waiting on
    // purpose — stacking a second wall over it is what this gate exists to stop.
    const poll = () => {
      const banner = document.getElementById('stb-consent');
      if (banner && banner.hidden === false) {
        this.#consentTimer = window.setTimeout(poll, 1000);
        return;
      }
      fire();
    };
    this.#consentTimer = window.setTimeout(poll, 6000);
  }

  // Paid landing: open only once the visitor has engaged (scrolled past half a
  // viewport) or after a long fallback — whichever comes first. Product first.
  #armEngagementTrigger() {
    const fallback = parseInt(this.getAttribute('paid-delay'), 10) || 25000;
    const fire = () => {
      window.removeEventListener('scroll', onScroll);
      if (this.#timer) {
        window.clearTimeout(this.#timer);
        this.#timer = null;
      }
      this.open();
    };
    const onScroll = () => {
      const scrolled =
        window.scrollY || document.documentElement.scrollTop || 0;
      if (scrolled > window.innerHeight * 0.5) fire();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true,
      signal: this.#abort.signal,
    });
    this.#timer = window.setTimeout(fire, fallback);
  }

  open() {
    if (this.#open) return;
    const designMode = window.Shopify && window.Shopify.designMode;
    // Mutex: never open over another popup that's already showing. Skip the
    // gate in the editor / preview, where opening is a deliberate, one-at-a-time
    // action. A blocked instance simply doesn't open (and doesn't mark itself
    // "seen"), so an exit popup blocked by the welcome modal can still fire
    // later once the modal is closed.
    if (!designMode && !this.#previewMode && openPopup && openPopup !== this) return;

    // Same rule, extended past this module: never open over a native <dialog>
    // that is already showing — the cart sheet above all, but the search and
    // menu drawers too. Like the mutex above, a blocked instance simply doesn't
    // open and doesn't mark itself seen, so it can still fire later.
    if (!designMode && !this.#previewMode && document.querySelector('dialog[open]')) return;

    this.#open = true;
    if (!designMode && !this.#previewMode) {
      openPopup = this;
      // Don't re-ask someone who already answered on an earlier visit.
      this.#restoreChoice();
    }
    this.#lastFocus = document.activeElement;
    this.setAttribute('open', '');
    this.dialog?.setAttribute('aria-hidden', 'false');

    // Lock background scroll while the overlay is up.
    this.#prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    const target = this.#focusables()[0] || this.dialog;
    window.requestAnimationFrame(() => target?.focus());

    // Showing it counts as "seen" — suppress for the configured window.
    // Skip in the editor and in preview mode so neither pollutes real visits.
    if (!designMode && !this.#previewMode) {
      this.#persist();
      // A re-open onto the reveal or an error is the same view, not a new one.
      if (!this.#resumed) this.#track('view');
    }
  }

  close(restoreFocus) {
    if (!this.#open) return;
    // Dismissing is the only thing that retires the reveal — see REVEAL_KEY.
    this.#clearRevealed();
    this.#open = false;
    if (openPopup === this) openPopup = null;
    this.removeAttribute('open');
    this.dialog?.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = this.#prevOverflow;
    if (restoreFocus && this.#lastFocus && this.#lastFocus.focus) {
      this.#lastFocus.focus();
    }
  }

  // ── A/B test ────────────────────────────────────────────────────────────
  // Only present while a test runs (ab-arms). Every arm hands out the same
  // code; what differs is the route to it — straight to the email ('plain'),
  // a one-tap question first ('fitd'), or the offer kept a surprise until the
  // email is in ('mystery'). The arm is tagged onto the contact, so sign-ups
  // and later orders can be counted per arm in Shopify, and logged to Clarity
  // as views and sign-ups for the rate.
  #applyArm() {
    const arms = this.#parseArms();
    if (!arms.length) return;

    const designMode = window.Shopify && window.Shopify.designMode;
    const forced = new URLSearchParams(location.search).get('popup_arm');
    let arm = null;
    if (forced && arms.some((a) => a.name === forced)) {
      arm = forced;
      this.#armForced = true;
    } else if (designMode) {
      arm = this.getAttribute('ab-editor-arm') || arms[0].name;
      this.#armForced = true;
    } else {
      arm = this.#stickyArm(arms);
    }
    if (!arm) return;

    this.#arm = arm;
    this.setAttribute('data-arm', arm);

    const tagField = this.querySelector('[data-popup-tags]');
    if (tagField) {
      const base = [tagField.getAttribute('data-base-tags'), `${this.#abTest()}-${arm}`]
        .filter(Boolean)
        .join(', ');
      tagField.setAttribute('data-base-tags', base);
      tagField.value = base;
    }

    // One-step arms skip the question. Not stored as an answer, so it can't
    // leak into a later test that does ask.
    if (arm !== AB_GATE_ARM) {
      this.setAttribute('data-choice', 'self');
      const title = this.#forArm('[data-branch="self"] [data-popup-branch-title]');
      if (title && title.id) this.dialog?.setAttribute('aria-labelledby', title.id);
    }
  }

  #abTest() {
    return this.getAttribute('ab-test') || 'ab';
  }

  #parseArms() {
    return (this.getAttribute('ab-arms') || '')
      .split(',')
      .map((part) => {
        const [name, weight] = part.split(':');
        return { name: (name || '').trim(), weight: parseFloat(weight) || 0 };
      })
      .filter((arm) => /^[a-z]+$/.test(arm.name));
  }

  // Random once per visitor per test, then fixed. An arm turned down to 0 is
  // retired: its visitors are re-split across the arms still running.
  #stickyArm(arms) {
    const live = arms.filter((a) => a.weight > 0);
    if (!live.length) return null;
    const test = this.#abTest();
    try {
      const saved = JSON.parse(window.localStorage.getItem(AB_KEY) || 'null');
      if (saved && saved.test === test && live.some((a) => a.name === saved.arm)) {
        return saved.arm;
      }
    } catch (error) {
      /* unreadable — assign afresh */
    }
    let roll = Math.random() * live.reduce((sum, a) => sum + a.weight, 0);
    const arm = (live.find((a) => (roll -= a.weight) < 0) || live[live.length - 1]).name;
    try {
      window.localStorage.setItem(AB_KEY, JSON.stringify({ test, arm, at: Date.now() }));
    } catch (error) {
      /* private mode — re-rolled each page, but each sign-up is still tagged right */
    }
    return arm;
  }

  // The element for this visitor's arm: an arm-only copy if one exists,
  // otherwise the shared one.
  #forArm(selector) {
    const all = [...this.querySelectorAll(selector)];
    const arm = this.#arm;
    return (
      (arm && all.find((el) => el.getAttribute('data-arm-only') === arm)) ||
      all.find(
        (el) => !el.hasAttribute('data-arm-only') && el.getAttribute('data-arm-not') !== arm
      ) ||
      null
    );
  }

  // Clarity gets a session tag (filterable) and a per-arm event (countable).
  // Clarity can load after this element, so wait for it briefly rather than
  // drop the event. Forced arms (editor, ?popup_arm) are never counted.
  #track(action, attempt = 0) {
    if (!this.#arm || this.#armForced) return;
    const test = this.#abTest();
    const arm = this.#arm;
    if (attempt === 0) {
      try {
        window.Shopify?.analytics?.publish?.('stuburt_popup_ab', { test, arm, action });
      } catch (error) {
        /* no pixel bus on this page */
      }
    }
    if (typeof window.clarity !== 'function') {
      if (attempt < 10) window.setTimeout(() => this.#track(action, attempt + 1), 1000);
      return;
    }
    try {
      window.clarity('set', test, arm);
      window.clarity('event', `${test}-${action}-${arm}`);
    } catch (error) {
      /* analytics must never break the popup */
    }
  }

  // ── Two-step gate ───────────────────────────────────────────────────────
  // Recording the answer does three things: swaps the dialog to that branch's
  // copy (CSS keys off data-choice), appends the branch tag so the list
  // segments into gift buyers vs self buyers, and remembers the answer so a
  // returning visitor gets the email step instead of the question again.
  #choose(choice, tag, focusEmail) {
    if (!choice || !/^[a-z-]+$/.test(choice)) return;
    this.setAttribute('data-choice', choice);

    const tagField = this.querySelector('[data-popup-tags]');
    if (tagField) {
      const base = tagField.getAttribute('data-base-tags') || '';
      tagField.value = [base, tag].filter(Boolean).join(', ');
    }

    // Re-point the dialog's label at the heading that's now on screen — but not
    // when the reveal owns the dialog, or restoring the branch would relabel it
    // with a heading [submitted] has already hidden.
    if (!this.hasAttribute('submitted')) {
      const title = this.querySelector(
        `[data-branch="${choice}"] [data-popup-branch-title]`
      );
      if (title && title.id) {
        this.dialog?.setAttribute('aria-labelledby', title.id);
      }
    }

    this.#persistChoice(choice);

    if (focusEmail) {
      const email = this.form?.querySelector('input[type="email"]');
      window.requestAnimationFrame(() => email?.focus());
    }
  }

  // Re-apply a previous visit's answer. Deliberately skipped in the editor and
  // in ?preview_popup mode, so both always open on the question.
  #restoreChoice() {
    if (this.hasAttribute('data-choice')) return;
    const stored = this.#storedChoice();
    if (!stored) return;
    const button = this.querySelector(`[data-popup-choice="${stored}"]`);
    if (!button) return;
    this.#choose(stored, button.getAttribute('data-popup-choice-tag'), false);
  }

  #choiceKey() {
    return this.getAttribute('choice-storage-key') || '';
  }

  #storedChoice() {
    const key = this.#choiceKey();
    if (!key) return null;
    try {
      const value = window.localStorage.getItem(key);
      return value && /^[a-z-]+$/.test(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  #persistChoice(choice) {
    const key = this.#choiceKey();
    if (!key) return;
    try {
      window.localStorage.setItem(key, choice);
    } catch (error) {
      /* private mode — the question is simply asked again next visit */
    }
  }

  // Viewport gate for `disable-below`. Missing, blank or unparseable means no
  // gate at all, so the welcome popup — which never sets it — is untouched.
  #belowDisableWidth() {
    const max = parseInt(this.getAttribute('disable-below'), 10);
    if (!max) return false;
    return window.matchMedia(`(max-width: ${max}px)`).matches;
  }

  #storageKey() {
    return this.getAttribute('storage-key') || 'stuburt_popup';
  }

  #frequencyDays() {
    return parseInt(this.getAttribute('frequency-days'), 10) || 30;
  }

  #suppressed() {
    try {
      // Permanent, and checked before the frequency window: once the offer has
      // been claimed there is nothing left to pitch, however long ago it was.
      if (window.localStorage.getItem(OFFER_CLAIMED_KEY)) return true;

      const raw = window.localStorage.getItem(this.#storageKey());
      const ts = raw ? parseInt(raw, 10) : 0;
      if (!ts) return false;
      return Date.now() - ts < this.#frequencyDays() * 86400000;
    } catch (error) {
      return false;
    }
  }

  #persist() {
    try {
      window.localStorage.setItem(this.#storageKey(), String(Date.now()));
    } catch (error) {
      /* private mode / storage disabled — popup just shows every visit */
    }
  }

  #focusables() {
    const selector =
      'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return [...this.querySelectorAll(selector)].filter(
      (el) => el.offsetParent !== null
    );
  }

  #trapFocus(event) {
    if (event.key !== 'Tab' || !this.#open) return;
    const focusable = this.#focusables();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // The form posts NATIVELY — it must never be fetch-intercepted. This store
  // runs Shopify spam protection, and its reCAPTCHA token is attached only by
  // Shopify's own submit flow: a hand-rolled fetch is rejected with 400
  // "Missing CAPTCHA token" while Shopify's parallel native submit quietly
  // succeeds. That showed "Something went wrong" over a signup that had in fact
  // gone through (errors.md, 2026-07-18) — and here it was worse than in the
  // footer, because the discount code was revealed only on the fetch's success,
  // so a visitor who had just handed over their email was left with nothing.
  // Stamp the session, let the browser navigate, resume from server state.
  #submit(event) {
    if (window.Shopify && window.Shopify.designMode) {
      // A native submit would navigate the customizer preview away — in the
      // editor just show the reveal so the merchant can see that state.
      event.preventDefault();
      this.#reveal();
      return;
    }
    const errorEl = this.querySelector('[data-popup-error]');
    if (errorEl) errorEl.textContent = '';
    this.#stampSubmit();
  }

  // ── Native submit, picked back up after the reload ──────────────────────
  // Returns true when it has taken the popup over, so connectedCallback stops
  // before arming its own trigger.
  #resumeAfterSubmit() {
    const marker = this.querySelector('[data-popup-autoshow]');
    if (!marker) return false;
    if (this.#readSubmitStamp() !== this.#storageKey()) return false;
    this.#resumed = true;

    // The reloaded page may have landed on the form's anchor. The popup is about
    // to cover it and lock scrolling anyway, so put the page back at the top
    // rather than let the visitor watch it lurch.
    window.scrollTo(0, 0);

    const state = marker.getAttribute('data-popup-autoshow');
    if (state === 'success' || state === 'subscribed') {
      // Someone already on the list gets the code too. They did what was asked,
      // and withholding a fixed public code only earns a support email.
      this.#markRevealed();
      // Counted here, after the reload, not on submit: an event fired as the
      // page navigates away is often never sent.
      this.#track(state === 'success' ? 'signup' : 'existing');
    } else {
      const errorEl = this.querySelector('[data-popup-error]');
      if (errorEl) {
        errorEl.textContent =
          marker.getAttribute('data-popup-autoshow-text') ||
          this.getAttribute('error-text') ||
          'Something went wrong. Please try again.';
      }
    }

    window.requestAnimationFrame(() => {
      // Cleared a frame late on purpose: stuburt-signup-toast.js reads the same
      // stamp synchronously as it connects, and needs to still see it so it
      // doesn't fire its own toast over this reveal.
      this.#clearSubmitStamp();
      this.open();
    });
    return true;
  }

  #stampSubmit() {
    try {
      window.sessionStorage.setItem(
        SUBMIT_STAMP_KEY,
        `${this.#storageKey()}:${Date.now()}`
      );
    } catch (error) {
      /* private mode — the signup still lands, only the reveal is lost */
    }
  }

  // The storage key of the popup that posted, or null if the stamp is missing
  // or too old to belong to this page load.
  #readSubmitStamp() {
    try {
      const raw = window.sessionStorage.getItem(SUBMIT_STAMP_KEY) || '';
      const split = raw.lastIndexOf(':');
      if (split < 1) return null;
      const at = parseInt(raw.slice(split + 1), 10);
      if (!at || Date.now() - at > SUBMIT_STAMP_MAX_AGE) return null;
      return raw.slice(0, split);
    } catch (error) {
      return null;
    }
  }

  #clearSubmitStamp() {
    try {
      window.sessionStorage.removeItem(SUBMIT_STAMP_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  // Swap to the reveal and re-point the dialog's label: [submitted] hides both
  // the question and the branch heading, so whichever one aria-labelledby named
  // is no longer on screen.
  #markRevealed(persist = true) {
    this.setAttribute('submitted', '');
    const title = this.#forArm('[data-popup-reveal-title]');
    if (title && title.id) this.dialog?.setAttribute('aria-labelledby', title.id);
    if (persist) this.#persistRevealed();
  }

  #persistRevealed() {
    try {
      window.sessionStorage.setItem(
        REVEAL_KEY,
        JSON.stringify({
          key: this.#storageKey(),
          path: window.location.pathname,
          at: Date.now(),
        })
      );
    } catch (error) {
      /* private mode — the reveal simply won't survive a rebuild */
    }
  }

  #revealedEarlier() {
    try {
      const raw = window.sessionStorage.getItem(REVEAL_KEY);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      return Boolean(
        saved &&
          saved.key === this.#storageKey() &&
          saved.path === window.location.pathname &&
          Date.now() - saved.at <= REVEAL_MAX_AGE
      );
    } catch (error) {
      return false;
    }
  }

  #clearRevealed() {
    try {
      window.sessionStorage.removeItem(REVEAL_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  #reveal() {
    this.#markRevealed();
    const reveal = this.querySelector('[data-popup-reveal]');
    window.requestAnimationFrame(() => {
      const target = reveal?.querySelector('a,button') || reveal;
      target?.focus?.();
    });
  }

  async #copy(button) {
    const code = button.getAttribute('data-code') || '';
    if (!code || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(code);
      const done = button.getAttribute('data-copied-label') || 'Copied';
      const original = button.textContent;
      button.textContent = done;
      window.setTimeout(() => {
        button.textContent = original;
      }, 2000);
    } catch (error) {
      /* clipboard blocked — leave the code visible to copy by hand */
    }
  }
}

if (!customElements.get('stuburt-popup')) {
  customElements.define('stuburt-popup', StuburtPopup);
}
